import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { numberToMoney } from "@/util/moneyConversion";
import type { FastOrderOutput } from "@/interfaces/fastOrders";
import { TrendingUp, ShoppingBag, CreditCard, AlertCircle } from "lucide-react";

interface FastOrderTotalsProps {
  orders?: FastOrderOutput[];
}

export function FastOrderTotals({ orders }: Readonly<FastOrderTotalsProps>) {
  const totalOrders = orders?.length ?? 0;

  const totalValue =
    orders?.reduce((sum, order) => sum + (order.totalAmount || 0), 0) ?? 0;

  const averageOrderValue = totalOrders > 0 ? totalValue / totalOrders : 0;

  const pendingPayments =
    orders?.filter((order) => {
      if (!order.payments || !Array.isArray(order.payments)) return true;
      const paidAmount = order.payments.reduce(
        (acc, curr) => acc + (curr.amount || 0),
        0
      );
      return (order.totalAmount || 0) - paidAmount > 0;
    }).length ?? 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <Card className="shadow-sm bg-white">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
          <CardTitle className="text-sm font-medium text-gray-600">
            Total de Pedidos
          </CardTitle>
          <div className="rounded-full p-2 bg-[#EE7B11]/10 text-[#EE7B11]">
            <ShoppingBag className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalOrders}</div>
          <p className="text-xs text-gray-500 mt-1">Pedidos registrados</p>
        </CardContent>
      </Card>

      <Card className="shadow-sm bg-white">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
          <CardTitle className="text-sm font-medium text-gray-600">
            Valor Total
          </CardTitle>
          <div className="rounded-full p-2 bg-green-50 text-green-600">
            <CreditCard className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{numberToMoney(totalValue)}</div>
          <p className="text-xs text-gray-500 mt-1">Valor total dos pedidos</p>
        </CardContent>
      </Card>

      <Card className="shadow-sm bg-white">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
          <CardTitle className="text-sm font-medium text-gray-600">
            Valor Médio
          </CardTitle>
          <div className="rounded-full p-2 bg-purple-50 text-purple-600">
            <TrendingUp className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {numberToMoney(averageOrderValue)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Valor médio por pedido</p>
        </CardContent>
      </Card>

      <Card className="shadow-sm bg-white">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-6">
          <CardTitle className="text-sm font-medium text-gray-600">
            Pagamentos Pendentes
          </CardTitle>
          <div className="rounded-full p-2 bg-amber-50 text-amber-600">
            <AlertCircle className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{pendingPayments}</div>
          <p className="text-xs text-gray-500 mt-1">
            Pedidos aguardando pagamento
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
